import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { healthCyclesAPI } from '../../services/api'
import { cyclePath } from './useStartHealthCycle'
import { formatDate } from './cycleStatus'

const TITLE_KEY = {
  doing: 'healthCycle.state.doing',
  measuring: 'healthCycle.state.measuring',
  none: 'healthCycle.state.none',
}
const REVIEW_KEY = {
  not_started: 'healthCycle.state.reviewingNotStarted',
  preparing: 'healthCycle.state.reviewingPreparing',
  ready: 'healthCycle.state.reviewingReady',
}

/** Planned or suggested date of the next blood test; nothing is stored until confirmed. */
function NextTestLine({ agreed }) {
  const { t, i18n } = useTranslation()
  const queryClient = useQueryClient()
  const [editing, setEditing] = useState(false)
  const [value, setValue] = useState(agreed.planned_draw_date || agreed.suggested_draw_date || '')
  const save = useMutation({
    mutationFn: (day) => healthCyclesAPI.setPlannedDraw(agreed.id, `${day}T08:00:00`, i18n.language),
    onSuccess: () => {
      setEditing(false)
      queryClient.invalidateQueries({ queryKey: ['healthCycleState'] })
      queryClient.invalidateQueries({ queryKey: ['healthCycleTestChecklist', agreed.id] })
    },
  })
  const date = (d) => formatDate(`${d}T12:00:00`, i18n.language)
  const planned = agreed.planned_draw_date

  let line
  if (planned && agreed.overdue) line = t('healthCycle.state.overdue', { date: date(planned) })
  else if (planned && agreed.days_to_draw === 0) line = t('healthCycle.state.nextTestToday', { date: date(planned) })
  else if (planned) line = t('healthCycle.state.nextTestOn', { date: date(planned), count: agreed.days_to_draw })
  else line = t('healthCycle.state.suggested', { date: date(agreed.suggested_draw_date) })

  return (
    <div className="text-sm" data-testid="cycle-next-test">
      <p className="text-gray-500">{t('healthCycle.state.nextTest')}</p>
      <p className={agreed.overdue ? 'text-red-700 font-medium' : 'text-gray-900'}>{line}</p>
      {editing ? (
        <form
          className="flex flex-wrap items-center gap-2 mt-2"
          onSubmit={(e) => { e.preventDefault(); if (value) save.mutate(value) }}
        >
          <input
            type="date"
            className="text-sm"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            aria-label={t('healthCycle.state.nextTest')}
            required
          />
          <button type="submit" className="btn-primary text-xs" disabled={save.isPending}>{t('healthCycle.state.save')}</button>
          <button type="button" className="btn-outline text-xs" onClick={() => setEditing(false)}>{t('healthCycle.state.cancel')}</button>
        </form>
      ) : (
        <div className="flex flex-wrap gap-2 mt-2">
          {!planned && (
            <button type="button" className="btn-primary text-xs" disabled={save.isPending}
              onClick={() => save.mutate(agreed.suggested_draw_date)}>
              {t('healthCycle.state.confirm')}
            </button>
          )}
          <button type="button" className="btn-outline text-xs" onClick={() => setEditing(true)}>
            {t('healthCycle.state.change')}
          </button>
        </div>
      )}
      {save.isError && <p className="text-xs text-red-600 mt-1" role="alert">{t('healthCycle.state.saveError')}</p>}
    </div>
  )
}

// VH-247 stage 1: the Health cycles tab leads with where the user stands — Reto 2026-11-04:
// "I do not understand the health cycle." The tab used to lead with "Start a health cycle".
export function CycleStateCard({ state, onStartWaiting }) {
  const { t, i18n } = useTranslation()
  const agreed = state.agreed_cycle
  const title = state.state === 'reviewing' ? t(REVIEW_KEY[state.review]) : t(TITLE_KEY[state.state])
  const date = (d) => formatDate(`${d}T12:00:00`, i18n.language)

  return (
    <section className="card" data-testid="cycle-state" data-state={state.state}>
      <div className="card-body space-y-4">
        <h2 className="text-lg font-semibold text-gray-900">{title}</h2>

        {state.state === 'none' && (
          <div className="space-y-3">
            <p className="text-sm text-gray-600">{t('healthCycle.state.noneHint')}</p>
            <Link to="/blood-tests" className="btn-primary text-sm inline-block">{t('healthCycle.state.uploadTest')}</Link>
          </div>
        )}

        {state.state === 'reviewing' && (
          <div className="space-y-3">
            <p className="text-sm text-gray-600">{t('healthCycle.state.waitingTest', { date: date(state.waiting_test.test_date) })}</p>
            {state.review === 'not_started' ? (
              onStartWaiting
            ) : (
              <Link to={cyclePath(state.cycle.id)} className="btn-primary text-sm inline-block">
                {t('healthCycle.state.openReview')}
              </Link>
            )}
          </div>
        )}

        {agreed && (
          <div className={`space-y-3 ${state.state === 'reviewing' ? 'border-t border-gray-100 pt-3' : ''}`}>
            <div className="text-sm">
              <p className="text-gray-900">
                {agreed.week_of_plan
                  ? t('healthCycle.state.agreedOn', { date: date(agreed.agreed_on), week: agreed.week_of_plan })
                  : t('healthCycle.state.fromTest', { date: date(agreed.blood_test_date) })}
              </p>
              {agreed.week_of_plan && (
                <p className="text-gray-500">{t('healthCycle.state.fromTest', { date: date(agreed.blood_test_date) })}</p>
              )}
            </div>
            {agreed.change_pending && (
              <p className="text-sm text-yellow-800 bg-yellow-50 rounded-md p-2" data-testid="change-pending">
                {t('healthCycle.state.changePending')}{' '}
                <Link to={cyclePath(agreed.id)} className="font-medium underline">{t('healthCycle.state.agreeChange')}</Link>
              </p>
            )}
            {state.state !== 'reviewing' && <NextTestLine key={agreed.planned_draw_date || 'none'} agreed={agreed} />}
            <div className="flex flex-wrap gap-3 text-sm">
              <Link to={cyclePath(agreed.id)} className="font-medium text-primary-600 hover:text-primary-700">
                {state.state === 'measuring' ? t('healthCycle.state.prepare') : t('healthCycle.state.openPlan')}
              </Link>
            </div>
          </div>
        )}
      </div>
    </section>
  )
}

export default CycleStateCard
